import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import materialsBg from "@/assets/ritual-materials.jpg?format=webp";
import yarn1Img from "@/assets/yarn1.jpg?format=webp";
import yarn2Img from "@/assets/yarn2.jpg?format=webp";
import yarn3Img from "@/assets/yarn3.jpg?format=webp";

// ─── Types ────────────────────────────────────────────────────────────────────

type Property = { label: string; value: string };
type MaterialImage = { src: string; alt: string };

type Material = {
  id: string;
  index: string;
  shortName: string;
  fullName: string;
  tag: string;
  tagline: string;
  origin: string;
  certification: string;
  description: string;
  properties: Property[];
  ideal: string;
  images: MaterialImage[];
};

// ─── Data ────────────────────────────────────────────────────────────────────

const materials: Material[] = [
  {
    id: "lotus",
    index: "01",
    shortName: "Lotus Silk",
    fullName: "Hand-woven Lotus Silk",
    tag: "The rarest",
    tagline: "Lighter than air. Stronger than desire.",
    origin: "Siem Reap, Cambodia",
    certification: "GOTS Certified · Zero-waste · Vegan",
    description:
      "Extracted strand by strand from lotus stems gathered at dawn, before the sun dries the filament. I travel to Cambodia personally to meet the artisans who spin it. Its properties are unlike any other fiber on earth — simultaneously ethereal and impossibly strong. Very few people in the world know how to make it.",
    properties: [
      {
        label: "Touch",
        value:
          "Cloud-like. Cool against the skin. A natural luster that shifts with every movement and every light.",
      },
      {
        label: "Weight",
        value: "Featherlight — almost as if you are wearing air rather than a gown.",
      },
      {
        label: "Care",
        value:
          "Dry clean only. Store away from direct sunlight to preserve the luster indefinitely.",
      },
      {
        label: "Sustainability",
        value: "Zero chemicals. Zero waste. Every stem returns to the water. Entirely vegan.",
      },
    ],
    ideal:
      "For brides who want to feel like they are wearing light. For warm destinations, outdoor ceremonies, and those who value the rarest things above all else.",
    images: [
      {
        src: yarn1Img,
        alt: "Lotus silk yarn, hand-threaded at dawn",
      },
      {
        src: yarn1Img,
        alt: "Fine lotus silk fiber being spun by hand",
      },
      {
        src: yarn1Img,
        alt: "Hand-woven lotus silk on the loom",
      },
    ],
  },
  {
    id: "pima",
    index: "02",
    shortName: "Pima Cotton",
    fullName: "Organic Pima Cotton",
    tag: "The grounded one",
    tagline: "Silky-smooth. Present without demanding attention.",
    origin: "Cañete Valley, Peru",
    certification: "GOTS & Fair Trade Certified",
    description:
      "Grown in the coastal valleys of Peru where the soil has been cultivated for generations. Pima cotton is prized for its exceptionally long fibers — which produce a fabric that is silky-smooth yet breathable, soft yet present. Each bale is hand-picked; nothing is bleached or chemically treated at any stage.",
    properties: [
      {
        label: "Touch",
        value:
          "Silky-smooth with a gentle warmth. Softer than standard cotton — almost like linen's more refined cousin.",
      },
      {
        label: "Weight",
        value:
          "Moderate. Substantial enough to drape beautifully, light enough to wear all day and into the night.",
      },
      {
        label: "Care",
        value: "Hand wash cold, or dry clean for the finished gown. Improves with each careful wash.",
      },
      {
        label: "Sustainability",
        value:
          "80% less water than conventional cotton. Rain-fed. Unbleached. Carbon-neutral supply chain.",
      },
    ],
    ideal:
      "For brides who want something grounded and elegant without ostentation. Works beautifully in any climate, for any season, for any kind of ceremony.",
    images: [
      {
        src: yarn2Img,
        alt: "Pima cotton yarn, long-staple threads aligned",
      },
      {
        src: yarn2Img,
        alt: "Organic pima cotton in the Cañete Valley fields",
      },
      {
        src: yarn2Img,
        alt: "Pima cotton fabric — close-up of the silky weave",
      },
    ],
  },
  {
    id: "linen",
    index: "03",
    shortName: "Heritage Linen",
    fullName: "Heritage Linen",
    tag: "The timeless one",
    tagline: "Holds shape. Softens with wear. Improves with every season.",
    origin: "Normandy, France",
    certification: "European Flax Certified",
    description:
      "Sourced from flax fields in Normandy cultivated for generations, field-retted in the slow, traditional way that no industrial process can replicate. This is a fiber with memory — it holds its shape, softens with wear, and grows more beautiful with time. Linen is the fabric of heirlooms. It has always been.",
    properties: [
      {
        label: "Touch",
        value:
          "Textured and honest. Slightly structured at first, it softens beautifully with every wear and every movement.",
      },
      {
        label: "Weight",
        value:
          "Medium-substantial. It moves with authority. It has a presence that lighter fabrics cannot match.",
      },
      {
        label: "Care",
        value:
          "Hand wash cold, or dry clean. Linen only improves with age — so does the care ritual around it.",
      },
      {
        label: "Sustainability",
        value:
          "Zero irrigation required. No chemical softeners. Full zero-waste fiber extraction from field to thread.",
      },
    ],
    ideal:
      "For brides who want a gown with character. For autumn and winter ceremonies, intimate settings, and those who love the way beautiful things age into something even better.",
    images: [
      {
        src: yarn3Img,
        alt: "Heritage linen yarn — flax threaded by hand",
      },
      {
        src: yarn3Img,
        alt: "Normandy flax field in bloom",
      },
      {
        src: yarn3Img,
        alt: "Woven heritage linen — the texture of centuries",
      },
    ],
  },
];

// ─── Compact Bento Card ───────────────────────────────────────────────────────

function BentoCard({
  material,
  onSelect,
  isSelected,
}: {
  material: Material;
  onSelect: () => void;
  isSelected: boolean;
}) {
  const [imgIdx, setImgIdx] = useState(0);

  // Auto-advance carousel; pause when card is hidden (selected/expanded)
  useEffect(() => {
    if (isSelected || material.images.length <= 1) return;
    const t = setInterval(
      () => setImgIdx((i) => (i + 1) % material.images.length),
      4500
    );
    return () => clearInterval(t);
  }, [isSelected, material.images.length]);

  return (
    <div
      onClick={onSelect}
      className="relative overflow-hidden cursor-pointer bg-card border border-border group h-full"
      style={{ visibility: isSelected ? "hidden" : "visible" }}
    >
      {/* ── Background image carousel ── */}
      <div className="absolute inset-0">
        {material.images.map((img, i) => (
          <div
            key={img.src}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              i === imgIdx ? "opacity-100" : "opacity-0"
            }`}
          >
            <img
              src={img.src}
              alt=""
              role="presentation"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        ))}
        {/* Dual gradient — covers both text zones while letting image breathe in the centre */}
        <div className="absolute inset-0 bg-gradient-to-t from-background/96 via-background/50 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/88 via-background/30 to-transparent" />
      </div>

      {/* Decorative background number */}
      <div className="absolute -bottom-4 -right-2 font-display text-[130px] leading-none text-foreground/[0.04] select-none pointer-events-none z-10">
        {material.index}
      </div>

      {/* Base top border */}
      <div className="absolute top-0 left-0 w-full h-px bg-gold/20 z-10" />
      {/* Sweeping gold accent on hover */}
      <div className="absolute top-0 left-0 h-px bg-gold/70 w-0 group-hover:w-full transition-[width] duration-700 ease-out z-10" />

      {/* Main content */}
      <div className="relative z-10 p-7 md:p-8 lg:p-10 h-full flex flex-col">
        {/* Top meta */}
        <div>
          <p className="font-body text-[8px] tracking-[0.38em] uppercase text-gold/80 mb-5">
            {material.index} — {material.tag}
          </p>
          <h3 className="font-display text-3xl md:text-4xl lg:text-5xl text-foreground leading-[1.05] mb-3">
            {material.shortName}
          </h3>
          <p className="font-body text-[8px] tracking-[0.2em] uppercase text-muted-foreground/65 mt-1">
            {material.origin}
          </p>
        </div>

        {/* Bottom */}
        <div className="mt-auto pt-8">
          <p className="font-body text-xs text-muted-foreground/80 leading-relaxed mb-5 max-w-[220px]">
            {material.tagline}
          </p>
          <div className="flex items-center gap-2">
            <span className="font-body text-[8px] tracking-[0.28em] uppercase text-gold/65 group-hover:text-gold transition-colors duration-300">
              Explore
            </span>
            <ArrowUpRight
              size={10}
              className="text-gold/65 group-hover:text-gold group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300"
            />
          </div>
        </div>
      </div>

      {/* Carousel dot indicators */}
      {material.images.length > 1 && (
        <div className="absolute bottom-4 right-5 z-20 flex items-center gap-1.5">
          {material.images.map((_, i) => (
            <button
              key={i}
              aria-label={`Image ${i + 1}`}
              onClick={(e) => {
                e.stopPropagation();
                setImgIdx(i);
              }}
              className="flex items-center justify-center p-3 -m-3"
            >
              <span className={`block h-[2px] rounded-full transition-all duration-400 ${
                i === imgIdx
                  ? "w-5 bg-gold"
                  : "w-[5px] bg-white/30 hover:bg-white/55"
              }`} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Expanded Detail Overlay ──────────────────────────────────────────────────

function ExpandedDetail({
  material,
  onClose,
  t,
}: {
  material: Material;
  onClose: () => void;
  t: (key: string) => string;
}) {
  const [imgIdx, setImgIdx] = useState(0);

  const prev = () =>
    setImgIdx((i) => (i - 1 + material.images.length) % material.images.length);
  const next = () =>
    setImgIdx((i) => (i + 1) % material.images.length);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.45, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="fixed inset-0 md:inset-5 lg:inset-10 z-50 bg-background border border-border flex flex-col md:flex-row overflow-hidden"
    >
      {/* Close button */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0, transition: { duration: 0.08 } }}
        transition={{ delay: 0.3 }}
        onClick={onClose}
        className="absolute top-5 right-5 z-20 w-11 h-11 flex items-center justify-center text-muted-foreground/50 hover:text-foreground transition-colors duration-200"
        aria-label="Close"
      >
        <X size={15} strokeWidth={1.5} />
      </motion.button>

      {/* ── Left: image carousel + identity panel ── */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0, transition: { duration: 0.08 } }}
        transition={{ delay: 0.2 }}
        className="md:w-[40%] lg:w-[36%] shrink-0 border-b md:border-b-0 md:border-r border-border flex flex-col overflow-hidden"
      >
        {/* Image carousel */}
        <div className="relative h-56 md:flex-1 overflow-hidden bg-card">
          {/* Images */}
          {material.images.map((img, i) => (
            <motion.div
              key={img.src}
              className="absolute inset-0"
              initial={{ opacity: 0 }}
              animate={{ opacity: i === imgIdx ? 1 : 0 }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
            >
              <img
                src={img.src}
                alt={img.alt}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </motion.div>
          ))}

          {/* Bottom gradient + caption */}
          <div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black/55 to-transparent pt-10 pb-10 px-5">
            <p className="font-body text-[7px] tracking-[0.16em] uppercase text-white/55 italic">
              {material.images[imgIdx]?.alt}
            </p>
          </div>

          {/* Prev / Next arrows */}
          {material.images.length > 1 && (
            <>
              <button
                onClick={prev}
                aria-label="Previous image"
                className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-11 h-11 flex items-center justify-center bg-black/30 hover:bg-black/55 text-white/70 hover:text-white transition-all duration-200"
              >
                <ChevronLeft size={13} strokeWidth={1.5} />
              </button>
              <button
                onClick={next}
                aria-label="Next image"
                className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-11 h-11 flex items-center justify-center bg-black/30 hover:bg-black/55 text-white/70 hover:text-white transition-all duration-200"
              >
                <ChevronRight size={13} strokeWidth={1.5} />
              </button>
            </>
          )}

          {/* Dot indicators */}
          {material.images.length > 1 && (
            <div className="absolute bottom-3 right-4 z-20 flex items-center gap-1.5">
              {material.images.map((_, i) => (
                <button
                  key={i}
                  aria-label={`Image ${i + 1}`}
                  onClick={() => setImgIdx(i)}
                  className="flex items-center justify-center p-3 -m-3"
                >
                  <span className={`block h-[2px] rounded-full transition-all duration-300 ${
                    i === imgIdx
                      ? "w-4 bg-gold"
                      : "w-[4px] bg-white/35 hover:bg-white/60"
                  }`} />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Identity text */}
        <div className="shrink-0 bg-card p-6 md:p-8 lg:p-10">
          <div className="w-8 h-px bg-gold mb-6" />
          <p className="font-body text-[8px] tracking-[0.38em] uppercase text-gold/50 mb-4">
            {material.index} — {material.tag}
          </p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-foreground leading-[1.05] mb-4">
            {material.fullName}
          </h2>
          <p className="font-body text-[8px] tracking-[0.2em] uppercase text-muted-foreground/35 mb-1.5">
            {material.origin}
          </p>
          <p className="font-body text-[7px] tracking-[0.18em] uppercase text-gold/30 leading-relaxed mb-5">
            {material.certification}
          </p>
          <div className="w-6 h-px bg-gold/25 mb-4" />
          <p className="font-body text-sm text-muted-foreground/45 italic leading-relaxed">
            {material.tagline}
          </p>
        </div>
      </motion.div>

      {/* ── Right: scrollable detail panel ── */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0, transition: { duration: 0.08 } }}
        transition={{ delay: 0.24 }}
        className="flex-1 overflow-y-auto p-8 md:p-12 lg:p-14"
      >
        <p className="font-body text-sm text-muted-foreground leading-relaxed mb-12 max-w-prose">
          {material.description}
        </p>

        {/* Properties */}
        <div className="mb-12">
          <p className="font-body text-[8px] tracking-[0.38em] uppercase text-gold/40 mb-8">
            {t("materials.propertiesLabel")}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-8">
            {material.properties.map((prop, i) => (
              <motion.div
                key={prop.label}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, transition: { duration: 0.06 } }}
                transition={{ delay: 0.32 + i * 0.06 }}
              >
                <p className="font-body text-[9px] tracking-[0.28em] uppercase text-gold mb-2">
                  {prop.label}
                </p>
                <p className="font-body text-xs text-foreground/65 leading-relaxed">
                  {prop.value}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Ideal for */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, transition: { duration: 0.06 } }}
          transition={{ delay: 0.52 }}
          className="border-l border-gold/25 pl-5"
        >
          <p className="font-body text-[9px] tracking-[0.25em] uppercase text-gold mb-3">
            {t("materials.idealLabel")}
          </p>
          <p className="font-body text-sm text-muted-foreground/70 leading-relaxed italic">
            {material.ideal}
          </p>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}

// ─── Section ─────────────────────────────────────────────────────────────────

const MaterialsSection = () => {
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = materials.find((m) => m.id === selectedId) ?? null;

  // Lock body scroll while overlay is open
  useEffect(() => {
    document.body.style.overflow = selectedId ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [selectedId]);

  const handleSelect = (id: string) => setSelectedId(id);
  const handleClose = () => setSelectedId(null);

  return (
    <section id="materials" className="w-full py-24 md:py-40 px-6 bg-background relative overflow-hidden">
      {/* Background image with heavy overlay */}
      <div className="absolute inset-0 -z-10">
        <img src={materialsBg} alt="" className="w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-background/90" />
      </div>
      <div className="max-w-5xl mx-auto">

        {/* ── Header ── */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, ease: "easeOut" }}
          className="mb-14"
        >
          <div className="w-12 h-px bg-gold mb-8" />
          <p className="font-body text-xs tracking-[0.3em] uppercase text-gold mb-4">{t("materials.overline")}</p>
          <h2 className="font-display text-4xl md:text-5xl text-foreground mb-5">
            {t("materials.title")}
          </h2>
          <p className="font-body text-sm text-muted-foreground leading-relaxed max-w-sm">
            {t("materials.subtitle")}
          </p>
        </motion.div>

        {/* ── Mobile: horizontal snap carousel ── */}
        <div className="flex md:hidden gap-3 overflow-x-auto pb-6 snap-x snap-mandatory -mx-6 px-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {materials.map((material) => (
            <div key={material.id} className="snap-start shrink-0 w-[78vw] min-h-[300px]">
              <BentoCard
                material={material}
                isSelected={selectedId === material.id}
                onSelect={() => handleSelect(material.id)}
              />
            </div>
          ))}
        </div>

        {/* ── Desktop: asymmetric bento grid ── */}
        <div className="hidden md:grid grid-cols-[3fr_2fr] grid-rows-[300px_300px] gap-3">
          {/* Card 1: tall — spans both rows */}
          <div className="row-span-2 h-full">
            <BentoCard
              material={materials[0]}
              isSelected={selectedId === materials[0].id}
              onSelect={() => handleSelect(materials[0].id)}
            />
          </div>
          {/* Card 2: top-right */}
          <div className="h-full">
            <BentoCard
              material={materials[1]}
              isSelected={selectedId === materials[1].id}
              onSelect={() => handleSelect(materials[1].id)}
            />
          </div>
          {/* Card 3: bottom-right */}
          <div className="h-full">
            <BentoCard
              material={materials[2]}
              isSelected={selectedId === materials[2].id}
              onSelect={() => handleSelect(materials[2].id)}
            />
          </div>
        </div>

        {/* Hint */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="font-body text-[8px] tracking-[0.25em] uppercase text-muted-foreground/25 mt-6 text-center"
        >
          {t("materials.selectPrompt")}
        </motion.p>

      </div>

      {/* ── Overlay ── */}
      <AnimatePresence>
        {selected && (
          <>
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 z-40 bg-black/55 backdrop-blur-sm"
              onClick={handleClose}
            />
            <ExpandedDetail
              key={selected.id}
              material={selected}
              onClose={handleClose}
              t={t}
            />
          </>
        )}
      </AnimatePresence>
    </section>
  );
};

export default MaterialsSection;
